import { All, AppActionTypes } from "@app/store/actions/app.actions";
import {
  LoadSolutionMatrixFailure,
  LoadSolutionMatrixStart,
  LoadSolutionMatrixSuccess
} from "@app/store/actions/solution.actions";
import { BackendConfigInterface } from "@core/interfaces/backend-config.interface";
import { CameraInterface } from "@core/interfaces/camera.interface";
import { CollectionInterface } from "@core/interfaces/collection.interface";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { GroupInterface } from "@core/interfaces/group.interface";
import { ImageThumbnailInterface } from "@core/interfaces/image-thumbnail.interface";
import { ImageInterface, FINAL_REVISION_LABEL, ORIGINAL_REVISION_LABEL } from "@core/interfaces/image.interface";
import { NestedCommentInterface } from "@core/interfaces/nested-comment.interface";
import { RemoteSourceAffiliateInterface } from "@core/interfaces/remote-source-affiliate.interface";
import { SolutionInterface } from "@core/interfaces/solution.interface";
import { SubscriptionInterface } from "@core/interfaces/subscription.interface";
import { TelescopeInterface } from "@core/interfaces/telescope.interface";
import { TogglePropertyInterface } from "@core/interfaces/toggle-property.interface";
import { UtilsService } from "@core/services/utils/utils.service";
import { BreadcrumbInterface } from "@shared/components/misc/breadcrumb/breadcrumb.interface";

export interface AppState {
  // Weather the app has been initialized.
  initialized: boolean;

  breadcrumb: BreadcrumbInterface[];

  currentFullscreenImage: ImageInterface["pk"] | null;
  currentFullscreenImageEvent: MouseEvent | TouchEvent | null;

  // The user's language.
  language: string;

  // The list of subscription objects (e.g. Lite, Premium, Ultimate.
  subscriptions: SubscriptionInterface[];

  // Constants and configuration items from the backend.
  backendConfig: BackendConfigInterface | null;

  // The country code of the user's location.
  requestCountry: string;

  // All seen content types.
  contentTypes: ContentTypeInterface[];

  // All seen images.
  images: ImageInterface[];

  // All seen thumbnails.
  thumbnails: ImageThumbnailInterface[];

  // All seen solutions.
  solutions: SolutionInterface[];

  // Map of solution matrices by solution ID.
  solutionMatrices: { [solutionId: number]: any };

  // Dictionary of solution IDs for which matrix loading is in progress
  solutionMatricesLoading: { [solutionId: number]: boolean };

  // All seen telescopes.
  telescopes: TelescopeInterface[];

  // All seen cameras.
  cameras: CameraInterface[];

  // This is what's been typed to create a new location.
  createLocationAddTag: string;

  // All seen nested comments.
  nestedComments: NestedCommentInterface[] | null;

  // All seen toggle properties.
  toggleProperties: TogglePropertyInterface[];

  // All seen remote source affiliates.
  remoteSourceAffiliates: RemoteSourceAffiliateInterface[] | null;

  // All seen groups.
  groups: GroupInterface[] | null;

  // All seen collections.
  collections: CollectionInterface[] | null;

  // Messages unread count
  messagesUnreadCount: number;
}

export const initialAppState: AppState = {
  initialized: false,
  breadcrumb: [],
  currentFullscreenImage: null,
  currentFullscreenImageEvent: null,
  language: "en",
  subscriptions: [],
  backendConfig: null,
  requestCountry: null,
  contentTypes: [],
  images: [],
  thumbnails: [],
  solutions: [],
  solutionMatrices: {},
  solutionMatricesLoading: {},
  telescopes: [],
  cameras: [],
  createLocationAddTag: null,
  nestedComments: null,
  toggleProperties: [],
  remoteSourceAffiliates: null,
  groups: null,
  collections: null,
  messagesUnreadCount: 0
};

function handleCreateTogglePropertySuccess(state: AppState, action: any): AppState {
  let image: ImageInterface = null;

  const imageContentType = findImageContentType(state);

  if (imageContentType && action.payload.toggleProperty.contentType === imageContentType.id) {
    image = findImage(state, action.payload.toggleProperty.objectId);

    if (image) {
      image = updateImageCounts(image, action.payload.toggleProperty.propertyType, true);
    }
  }

  return {
    ...state,
    toggleProperties: UtilsService.arrayUniqueObjects([...state.toggleProperties, action.payload.toggleProperty], "id"),
    images: image ? updateImages(state.images, image) : state.images
  };
}

function handleDeleteTogglePropertySuccess(state: AppState, action: any): AppState {
  let image: ImageInterface = null;

  const imageContentType = findImageContentType(state);

  if (imageContentType && action.payload.toggleProperty.contentType === imageContentType.id) {
    image = findImage(state, action.payload.toggleProperty.objectId);

    if (image) {
      image = updateImageCounts(image, action.payload.toggleProperty.propertyType, false);
    }
  }

  return {
    ...state,
    toggleProperties: state.toggleProperties.filter(property => property.id !== action.payload.toggleProperty.id),
    images: image ? updateImages(state.images, image) : state.images
  };
}

// Helper functions
function findImageContentType(state: AppState): ContentTypeInterface | undefined {
  return state.contentTypes.find(contentType => contentType.appLabel === "astrobin" && contentType.model === "image");
}

function findImage(state: AppState, objectId: number): ImageInterface | undefined {
  return state.images.find(image => image.pk === objectId);
}

function updateImageCounts(image: ImageInterface, propertyType: string, increment: boolean): ImageInterface {
  const countChange = increment ? 1 : -1;
  if (propertyType === "like") {
    return {
      ...image,
      likeCount: image.likeCount + countChange,
      currentUserLiked: increment
    };
  } else if (propertyType === "bookmark") {
    return {
      ...image,
      bookmarkCount: image.bookmarkCount + countChange,
      currentUserBookmarked: increment
    };
  }
  return image; // Default to return the same image if no changes
}

function updateImages(images: ImageInterface[], updatedImage: ImageInterface): ImageInterface[] {
  const imageIndex = images.findIndex(i => i.pk === updatedImage.pk);

  if (imageIndex === -1) {
    return images;
  }

  return [...images.slice(0, imageIndex), updatedImage, ...images.slice(imageIndex + 1)];
}

// Helper functions for collection ancestor updates
function getAncestorCollectionIds(collections: CollectionInterface[], collectionId: number): number[] {
  const ancestorIds: number[] = [];
  let currentCollection = collections.find(c => c.id === collectionId);

  while (currentCollection && currentCollection.parent !== null) {
    ancestorIds.push(currentCollection.parent);
    currentCollection = collections.find(c => c.id === currentCollection.parent);
  }

  return ancestorIds;
}

function updateAncestorCollections(
  collections: CollectionInterface[],
  collectionId: number,
  isWip: boolean,
  increment: boolean
): CollectionInterface[] {
  const ancestorIds = getAncestorCollectionIds(collections, collectionId);
  if (ancestorIds.length === 0) {
    return collections;
  }

  const countChange = increment ? 1 : -1;

  return collections.map(collection => {
    if (ancestorIds.includes(collection.id)) {
      // Update all ancestor collections
      return {
        ...collection,
        // Update imageCountIncludingNested (but only for non-WIP images)
        imageCountIncludingNested: !isWip
          ? Math.max(0, (collection.imageCountIncludingNested || 0) + countChange)
          : collection.imageCountIncludingNested,

        // Always update the WIP-including count
        imageCountIncludingWipIncludingNested: Math.max(
          0,
          (collection.imageCountIncludingWipIncludingNested || 0) + countChange
        )
      };
    }
    return collection;
  });
}

export function appReducer(state = initialAppState, action: All): AppState {
  switch (action.type) {
    case AppActionTypes.INITIALIZE_SUCCESS: {
      return {
        ...state,
        initialized: true,
        subscriptions: action.payload.subscriptions,
        backendConfig: action.payload.backendConfig,
        requestCountry: action.payload.requestCountry
      };
    }

    case AppActionTypes.SET_BREADCRUMB: {
      return {
        ...state,
        breadcrumb: action.payload.breadcrumb
      };
    }

    case AppActionTypes.SHOW_FULLSCREEN_IMAGE: {
      return {
        ...state,
        currentFullscreenImage: action.payload.imageId,
        currentFullscreenImageEvent: action.payload.event
      };
    }

    case AppActionTypes.HIDE_FULLSCREEN_IMAGE: {
      return {
        ...state,
        currentFullscreenImage: null
      };
    }

    case AppActionTypes.LOAD_CONTENT_TYPE_SUCCESS: {
      return {
        ...state,
        contentTypes: [...state.contentTypes.filter(i => i.id !== action.payload.id), ...[action.payload]]
      };
    }

    case AppActionTypes.SET_IMAGE:
    case AppActionTypes.LOAD_IMAGE_SUCCESS:
    case AppActionTypes.ACCEPT_COLLABORATOR_REQUEST_SUCCESS:
    case AppActionTypes.DENY_COLLABORATOR_REQUEST_SUCCESS:
    case AppActionTypes.REMOVE_COLLABORATOR_SUCCESS: {
      if (action.payload === null || action.payload === undefined) {
        return state;
      }

      const thumbnails = !!action.payload.thumbnails ? [...action.payload.thumbnails] : [];

      return {
        ...state,
        images: [...state.images.filter(i => i.pk !== action.payload.pk), action.payload],
        thumbnails: UtilsService.arrayUniqueObjects([...state.thumbnails, ...thumbnails], null, false),
        telescopes: action.payload.imagingTelescopes
          ? UtilsService.arrayUniqueObjects([...state.telescopes, ...action.payload.imagingTelescopes], "pk")
          : [],
        cameras: action.payload.imagingCameras
          ? UtilsService.arrayUniqueObjects([...state.cameras, ...action.payload.imagingCameras], "pk")
          : []
      };
    }

    // case AppActionTypes.SAVE_IMAGE_SUCCESS: {
    //   return {
    //     ...state,
    //     images: [
    //       ...state.images.filter((i) => i.pk !== action.payload.image.pk),
    //       action.payload.image as ImageEditModelInterface,
    //     ],
    //   };
    // }

    case AppActionTypes.LOAD_IMAGES_SUCCESS: {
      const flatImages = action.payload.results.map(image => ({
        ...image,
        imagingTelescopes: [],
        imagingCameras: [],
        thumbnails: []
      }));
      const images = UtilsService.arrayUniqueObjects([...state.images, ...flatImages], "pk");

      let thumbnails = [...state.thumbnails];

      action.payload.results.forEach(image => {
        if (!!image.thumbnails) {
          image.thumbnails.forEach(thumbnail => {
            thumbnails = UtilsService.arrayUniqueObjects([...thumbnails, ...image.thumbnails], null, false);
          });
        }
      });

      const telescopes = UtilsService.arrayUniqueObjects(
        [].concat.apply(
          state.telescopes,
          action.payload.results.filter(image => !!image.imagingTelescopes).map(image => image.imagingTelescopes)
        ),
        "pk"
      );

      const cameras = UtilsService.arrayUniqueObjects(
        [].concat.apply(
          state.cameras,
          action.payload.results.filter(image => !!image.imagingCameras).map(image => image.imagingCameras)
        ),
        "pk"
      );

      return {
        ...state,
        images,
        thumbnails,
        telescopes,
        cameras
      };
    }

    case AppActionTypes.SAVE_IMAGE_REVISION_SUCCESS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.revision.image);
      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      const updatedImage = {
        ...state.images[imageIndex],
        revisions: [
          ...state.images[imageIndex].revisions.filter(revision => revision.pk !== action.payload.revision.pk),
          action.payload.revision
        ].sort((a, b) => (a.uploaded > b.uploaded ? -1 : 1))
      };

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), updatedImage, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.PUBLISH_IMAGE_SUCCESS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.pk);
      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      const updatedImage = {
        ...state.images[imageIndex],
        isWip: false
      };

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), updatedImage, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.UNPUBLISH_IMAGE_SUCCESS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.pk);
      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      const updatedImage = {
        ...state.images[imageIndex],
        isWip: true
      };

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), updatedImage, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.MARK_IMAGE_AS_FINAL_SUCCESS: {
      const imagePk = action.payload.pk;
      const revisionLabel = action.payload.revisionLabel;

      const imageIndex = state.images.findIndex(i => i.pk === imagePk);
      if (imageIndex < 0) {
        return state;
      } // If image not found, return the original state

      // Create a deep copy of the image object
      const image = {
        ...state.images[imageIndex],
        revisions: state.images[imageIndex].revisions.map(revision => ({ ...revision }))
      };

      if (revisionLabel === ORIGINAL_REVISION_LABEL) {
        image.isFinal = true;
        image.revisions.forEach(revision => {
          revision.isFinal = false;
        });
        image.thumbnails.forEach(thumbnail => {
          thumbnail.revision = FINAL_REVISION_LABEL;
        });
      } else {
        image.isFinal = false;
        image.thumbnails.forEach(thumbnail => {
          thumbnail.revision = ORIGINAL_REVISION_LABEL;
        });
        image.revisions.forEach(revision => {
          revision.isFinal = revision.label === revisionLabel;
        });
      }

      // Return new state with the updated image
      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), image, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.DELETE_ORIGINAL_IMAGE_SUCCESS:
    case AppActionTypes.DELETE_IMAGE_UNCOMPRESSED_SOURCE_FILE_SUCCESS:
    case AppActionTypes.SUBMIT_IMAGE_FOR_IOTD_TP_CONSIDERATION_SUCCESS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.image.pk);
      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), action.payload.image, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.DELETE_IMAGE_REVISION_SUCCESS: {
      const imageIndex = state.images.findIndex(image =>
        image.revisions.some(revision => revision.pk === action.payload.pk)
      );
      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      const image = { ...state.images[imageIndex] };
      const revision = image.revisions.find(revision => revision.pk === action.payload.pk);
      image.revisions = image.revisions.filter(revision => revision.pk !== action.payload.pk);
      image.thumbnails = image.thumbnails.filter(thumbnail => thumbnail.revision !== revision.label);

      if (revision.isFinal) {
        image.isFinal = true;
        image.thumbnails = image.thumbnails.filter(thumbnail => thumbnail.revision !== FINAL_REVISION_LABEL);
        image.thumbnails = image.thumbnails
          .filter(thumbnail => thumbnail.revision === ORIGINAL_REVISION_LABEL)
          .map(thumbnail => ({
            ...thumbnail,
            revision: FINAL_REVISION_LABEL
          }));
      }

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), image, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.DELETE_IMAGE_SUCCESS: {
      return {
        ...state,
        images: state.images.filter(image => image.pk !== action.payload.pk)
      };
    }

    case AppActionTypes.UNDELETE_IMAGE_SUCCESS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.pk);

      if (imageIndex === -1) {
        return state;
      } // If the image is not found, return the original state

      const image = { ...state.images[imageIndex] };
      image.deleted = null;

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), image, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.LOAD_THUMBNAIL_SUCCESS: {
      return {
        ...state,
        thumbnails: UtilsService.arrayUniqueObjects([...state.thumbnails, action.payload], null, false)
      };
    }

    case AppActionTypes.LOAD_SOLUTION_SUCCESS: {
      const imageContentType = findImageContentType(state);
      let image: ImageInterface = null;

      if (imageContentType && action.payload.contentType === imageContentType.id) {
        image = findImage(state, parseInt(action.payload.objectId, 10));
        if (image) {
          image = {
            ...image,
            solution: action.payload
          };
        }
      }

      return {
        ...state,
        images: image
          ? [
              ...state.images.slice(
                0,
                state.images.findIndex(i => i.pk === image.pk)
              ),
              image,
              ...state.images.slice(state.images.findIndex(i => i.pk === image.pk) + 1)
            ]
          : state.images,
        solutions: UtilsService.arrayUniqueObjects([...state.solutions, action.payload], "id")
      };
    }

    case AppActionTypes.LOAD_SOLUTIONS_SUCCESS: {
      return {
        ...state,
        solutions: UtilsService.arrayUniqueObjects([...state.solutions, ...action.payload], "id")
      };
    }

    case AppActionTypes.LOAD_SOLUTION_MATRIX_START: {
      const matrixAction = action as unknown as LoadSolutionMatrixStart;

      return {
        ...state,
        solutionMatricesLoading: {
          ...state.solutionMatricesLoading,
          [matrixAction.payload.solutionId]: true
        }
      };
    }

    case AppActionTypes.LOAD_SOLUTION_MATRIX_SUCCESS: {
      const matrixAction = action as unknown as LoadSolutionMatrixSuccess;

      // Create new loading state object without the current solution ID
      const { [matrixAction.payload.solutionId]: _, ...newLoadingState } = state.solutionMatricesLoading;

      return {
        ...state,
        solutionMatrices: {
          ...state.solutionMatrices,
          [matrixAction.payload.solutionId]: matrixAction.payload.matrix
        },
        solutionMatricesLoading: newLoadingState
      };
    }

    case AppActionTypes.LOAD_SOLUTION_MATRIX_FAILURE: {
      const matrixAction = action as unknown as LoadSolutionMatrixFailure;

      // Create new loading state object without the current solution ID
      const { [matrixAction.payload.solutionId]: _, ...newLoadingState } = state.solutionMatricesLoading;

      return {
        ...state,
        solutionMatricesLoading: newLoadingState
      };
    }

    case AppActionTypes.LOAD_TELESCOPE_SUCCESS: {
      return {
        ...state,
        telescopes: [...state.telescopes.filter(i => i.pk !== action.payload.pk), action.payload]
      };
    }

    case AppActionTypes.LOAD_CAMERA_SUCCESS: {
      return {
        ...state,
        cameras: [...state.cameras.filter(i => i.pk !== action.payload.pk), action.payload]
      };
    }

    case AppActionTypes.CREATE_LOCATION_ADD_TAG: {
      return {
        ...state,
        createLocationAddTag: action.payload
      };
    }

    case AppActionTypes.LOAD_NESTED_COMMENTS_SUCCESS: {
      return {
        ...state,
        nestedComments: UtilsService.sortObjectsByProperty(
          UtilsService.arrayUniqueObjects([...(state.nestedComments || []), ...action.payload.nestedComments], "id"),
          "created"
        )
      };
    }

    case AppActionTypes.LOAD_NESTED_COMMENT_SUCCESS:
    case AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS:
    case AppActionTypes.UPDATE_NESTED_COMMENT_SUCCESS: {
      return {
        ...state,
        nestedComments: UtilsService.sortObjectsByProperty(
          UtilsService.arrayUniqueObjects([...(state.nestedComments || []), ...[action.payload.nestedComment]], "id"),
          "created"
        )
      };
    }

    case AppActionTypes.APPROVE_NESTED_COMMENT_SUCCESS: {
      return {
        ...state,
        nestedComments: state.nestedComments.map(comment =>
          comment.id === action.payload.nestedComment.id
            ? {
                ...comment,
                pendingModeration: false
              }
            : comment
        )
      };
    }

    case AppActionTypes.DELETE_NESTED_COMMENT_SUCCESS: {
      return {
        ...state,
        nestedComments: state.nestedComments.filter(comment => comment.id !== action.payload.id)
      };
    }

    case AppActionTypes.LOAD_TOGGLE_PROPERTY_SUCCESS: {
      if (action.payload.toggleProperty !== null) {
        return {
          ...state,
          toggleProperties: UtilsService.arrayUniqueObjects(
            [...state.toggleProperties, action.payload.toggleProperty],
            "id"
          )
        };
      }

      return state;
    }

    case AppActionTypes.CREATE_TOGGLE_PROPERTY_SUCCESS: {
      return handleCreateTogglePropertySuccess(state, action);
    }

    case AppActionTypes.DELETE_TOGGLE_PROPERTY_SUCCESS: {
      return handleDeleteTogglePropertySuccess(state, action);
    }

    case AppActionTypes.LOAD_REMOTE_SOURCE_AFFILIATES_SUCCESS: {
      return {
        ...state,
        remoteSourceAffiliates: action.payload.affiliates
      };
    }

    case AppActionTypes.LOAD_GROUPS_SUCCESS: {
      return {
        ...state,
        groups: action.payload.groups
      };
    }

    case AppActionTypes.LOAD_COLLECTIONS_SUCCESS: {
      return {
        ...state,
        collections: UtilsService.arrayUniqueObjects(
          [...(state.collections || []), ...action.payload.collections],
          "id"
        ).sort((a, b) => a.name.localeCompare(b.name))
      };
    }

    case AppActionTypes.FIND_COLLECTIONS_SUCCESS: {
      return {
        ...state,
        collections: UtilsService.arrayUniqueObjects(
          [...(state.collections || []), ...action.payload.response.results],
          "id"
        ).sort((a, b) => a.name.localeCompare(b.name))
      };
    }

    case AppActionTypes.CREATE_COLLECTION_SUCCESS: {
      // First, add the new collection to the collections array
      const updatedCollections = UtilsService.arrayUniqueObjects(
        [...(state.collections || []), action.payload.collection],
        "id"
      ).sort((a, b) => a.name.localeCompare(b.name));

      // Check if this collection has a parent
      if (action.payload.collection.parent !== null && state.collections) {
        // Find the parent collection
        const parentIndex = updatedCollections.findIndex(
          collection => collection.id === action.payload.collection.parent
        );

        if (parentIndex !== -1) {
          // Update the parent's nestedCollectionCount
          const parentCollection = updatedCollections[parentIndex];
          const updatedParentCollection = {
            ...parentCollection,
            nestedCollectionCount: (parentCollection.nestedCollectionCount || 0) + 1
          };

          // Replace the parent in the collections array
          updatedCollections[parentIndex] = updatedParentCollection;
        }
      }

      return {
        ...state,
        collections: updatedCollections
      };
    }

    case AppActionTypes.UPDATE_COLLECTION_SUCCESS: {
      return {
        ...state,
        collections: UtilsService.arrayUniqueObjects(
          [...state.collections.filter(i => i.id !== action.payload.collection.id), action.payload.collection],
          "id"
        ).sort((a, b) => a.name.localeCompare(b.name))
      };
    }

    case AppActionTypes.ADD_IMAGE_TO_COLLECTION_SUCCESS: {
      // First, check if the image is a WIP
      const image = state.images.find(img => img.pk === action.payload.imageId);
      const isWip = image ? image.isWip : false;

      // Update the direct collection first
      let updatedCollections = state.collections.map(collection =>
        collection.id === action.payload.collectionId
          ? {
              ...collection,
              images: [...(collection.images || []), action.payload.imageId],
              // Update direct collection counts
              imageCount: collection.imageCount !== undefined ? collection.imageCount + (isWip ? 0 : 1) : undefined,
              imageCountIncludingWip: (collection.imageCountIncludingWip || 0) + 1,
              // Update nested counts on the direct collection too
              imageCountIncludingNested: !isWip
                ? (collection.imageCountIncludingNested || 0) + 1
                : collection.imageCountIncludingNested,
              imageCountIncludingWipIncludingNested: (collection.imageCountIncludingWipIncludingNested || 0) + 1
            }
          : collection
      );

      // Then update all ancestor collections' nested counts
      updatedCollections = updateAncestorCollections(
        updatedCollections,
        action.payload.collectionId,
        isWip,
        true // increment
      );

      return {
        ...state,
        collections: updatedCollections
      };
    }

    case AppActionTypes.REMOVE_IMAGE_FROM_COLLECTION_SUCCESS: {
      // First, check if the image is a WIP
      const image = state.images.find(img => img.pk === action.payload.imageId);
      const isWip = image ? image.isWip : false;

      // Update the direct collection first
      let updatedCollections = state.collections.map(collection =>
        collection.id === action.payload.collectionId
          ? {
              ...collection,
              images: (collection.images || []).filter(i => i !== action.payload.imageId),
              // Update direct collection counts
              imageCount:
                collection.imageCount !== undefined ? Math.max(0, collection.imageCount - (isWip ? 0 : 1)) : undefined,
              imageCountIncludingWip: Math.max(0, (collection.imageCountIncludingWip || 1) - 1),
              // Update nested counts on the direct collection too
              imageCountIncludingNested: !isWip
                ? Math.max(0, (collection.imageCountIncludingNested || 1) - 1)
                : collection.imageCountIncludingNested,
              imageCountIncludingWipIncludingNested: Math.max(
                0,
                (collection.imageCountIncludingWipIncludingNested || 1) - 1
              )
            }
          : collection
      );

      // Then update all ancestor collections' nested counts
      updatedCollections = updateAncestorCollections(
        updatedCollections,
        action.payload.collectionId,
        isWip,
        false // decrement
      );

      return {
        ...state,
        collections: updatedCollections
      };
    }

    case AppActionTypes.SET_COLLECTION_COVER_IMAGE_SUCCESS: {
      return {
        ...state,
        collections: state.collections.map(collection =>
          collection.id === action.payload.collectionId
            ? {
                ...collection,
                coverThumbnail: action.payload.coverThumbnail,
                coverThumbnailHd: action.payload.coverThumbnailHd,
                squareCropping: action.payload.squareCropping,
                w: action.payload.w,
                h: action.payload.h
              }
            : collection
        )
      };
    }

    case AppActionTypes.DELETE_COLLECTION_SUCCESS: {
      // First, find the collection that's being deleted to check if it has a parent
      const collectionToDelete = state.collections.find(collection => collection.id === action.payload.collectionId);

      // Filter out the deleted collection
      const filteredCollections = state.collections.filter(collection => collection.id !== action.payload.collectionId);

      // If the deleted collection had a parent, update the parent's nestedCollectionCount
      if (collectionToDelete && collectionToDelete.parent !== null) {
        const parentIndex = filteredCollections.findIndex(collection => collection.id === collectionToDelete.parent);

        if (parentIndex !== -1) {
          // Update the parent's nestedCollectionCount
          const parentCollection = filteredCollections[parentIndex];
          const updatedParentCollection = {
            ...parentCollection,
            nestedCollectionCount: Math.max(0, (parentCollection.nestedCollectionCount || 1) - 1)
          };

          // Replace the parent in the collections array
          filteredCollections[parentIndex] = updatedParentCollection;
        }
      }

      return {
        ...state,
        collections: filteredCollections
      };
    }

    case AppActionTypes.UPDATE_ANNOTATIONS: {
      const imageIndex = state.images.findIndex(image => image.pk === action.payload.pk);
      if (imageIndex === -1) {
        return state;
      }

      const image = { ...state.images[imageIndex] };

      if (action.payload.revisionLabel) {
        // Update annotations in a specific revision
        const revisionIndex = image.revisions.findIndex(revision => revision.label === action.payload.revisionLabel);
        if (revisionIndex !== -1) {
          const updatedRevisions = [...image.revisions];
          updatedRevisions[revisionIndex] = {
            ...updatedRevisions[revisionIndex],
            annotations: action.payload.annotations
          };
          image.revisions = updatedRevisions;
        }
      } else {
        // Update annotations in the main image
        image.annotations = action.payload.annotations;
      }

      return {
        ...state,
        images: [...state.images.slice(0, imageIndex), image, ...state.images.slice(imageIndex + 1)]
      };
    }

    case AppActionTypes.FETCH_MESSAGES_UNREAD_COUNT_SUCCESS: {
      return {
        ...state,
        messagesUnreadCount: action.payload.count
      };
    }

    default: {
      return state;
    }
  }
}
